"use client";

import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useSetAtom, useAtomValue } from "jotai";
import {
  pickerHighlightAtom,
  activeFlowIdAtom,
  dynamicNodeConfigsAtom,
} from "@/store/editor-v2";
import { useSpotUser } from "@/utils/spotUser";
import { useSecretsSources } from "./useSecretsSources";
import { NodeDefinition } from "@/generated";
import { GraphNodeV2 } from "@/types/spotflow-v2";
import { Edge } from "@xyflow/react";
import {
  DataTypeBadge,
  DataChip,
  GLOBAL_VARIABLE_ICON_DATA_URI,
} from "@/icons/DataTypeIcon";
import {
  buildOptionsByValue,
  buildVariableOptions,
  inputPathHandleFor,
  type NodeOption,
  type OutputMeta,
  type OutputOption,
} from "./variableOptions";
import { normalizeDataType } from "./typeCompatibility";
import { ObjectShapeList } from "./ObjectShape";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";
import { Button } from "@/components/shadcn/button";
import { Checkbox } from "@/components/shadcn/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/shadcn/command";
import { cn } from "@/lib/utils";
import { ChevronDown, X, Check } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { Badge } from "@/components/shadcn/badge";
import { BoundVariableTooltipContent } from "./BoundVariableTooltip";
import ImageWithFallback from "@/components/ImageWithFallback";
import { NoxusIcon } from "@/components/UI/NoxusIcon";
import { VariableScopeBadge } from "./VariableScopeBadge";

// ── NodeIcon helper ───────────────────────────────────────────────────────────

// The badge on a coerced picker match: an object source stringifies to JSON,
// a scalar source (number/bool/datetime) to plain text.
function CoercedBadge({ dataType }: { dataType: string }) {
  const asJson = normalizeDataType(dataType) === "object";
  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className="px-1 text-[10px] leading-4 font-normal text-muted-foreground"
        >
          {asJson ? "as JSON" : "as text"}
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="top">
        {asJson
          ? "Inserted as JSON text in this field"
          : "Inserted as text in this field"}
      </TooltipContent>
    </Tooltip>
  );
}

function NodeIcon({
  image,
  iconName,
}: {
  color?: string;
  image?: string | null;
  iconName?: string | null;
  size?: number;
}) {
  if (iconName) return <NoxusIcon icon={iconName} size="xsm" />;
  if (image) {
    return (
      <div className="relative size-4 shrink-0">
        <ImageWithFallback
          fill
          style={{ objectFit: "contain" }}
          alt=""
          src={image}
        />
      </div>
    );
  }
  return <div className="size-4 shrink-0 rounded bg-muted" />;
}

// ── Shared props ──────────────────────────────────────────────────────────────

interface BaseProps {
  currentNodeId: string;
  nodes: GraphNodeV2[];
  edges: Edge[];
  nodeRegistry: NodeDefinition[];
  inputDataType?: string;
  /** When the target input is a list, only list-typed sources are offered. */
  inputIsList?: boolean;
  readOnly?: boolean;
  /** Name of the input binding key — used to highlight the target input on the canvas */
  inputName?: string;
}

interface SingleProps extends BaseProps {
  multiple?: false;
  value: string | undefined;
  onChange: (value: string | undefined, meta?: OutputMeta) => void;
}

interface MultiProps extends BaseProps {
  multiple: true;
  value: string[];
  onChange: (values: string[], metas: Record<string, OutputMeta>) => void;
  placeholder?: string;
}

type VariablePickerProps = SingleProps | MultiProps;

const CLEAR_HIGHLIGHT: import("@/store/editor-v2").PickerHighlight = {
  sourceNodeId: undefined,
  sourceOutputName: undefined,
  targetNodeId: undefined,
  targetInputName: undefined,
};

// ── Option builder — extracted for reuse by both modes ───────────────────────

export function useVariableOptions(
  nodes: GraphNodeV2[],
  currentNodeId: string,
  edges: Edge[],
  nodeRegistry: NodeDefinition[],
  inputDataType: string | undefined,
  inputName?: string,
  inputIsList?: boolean,
  inlineText?: boolean
): NodeOption[] {
  const dynamicNodeConfigs = useAtomValue(dynamicNodeConfigsAtom);
  const { spotGroup } = useSpotUser();
  const activeFlowId = useAtomValue(activeFlowIdAtom);
  const { secrets } = useSecretsSources(spotGroup?.id, activeFlowId);
  const restrictToHandle = inputPathHandleFor(
    nodes,
    currentNodeId,
    nodeRegistry,
    inputName
  );
  return useMemo(
    () =>
      buildVariableOptions({
        nodes,
        currentNodeId,
        edges,
        nodeRegistry,
        inputDataType,
        inputIsList,
        dynamicNodeConfigs,
        secrets,
        restrictToHandle,
        inputName,
        inlineText,
      }),
    [
      nodes,
      currentNodeId,
      edges,
      nodeRegistry,
      inputDataType,
      inputIsList,
      dynamicNodeConfigs,
      secrets,
      restrictToHandle,
      inputName,
      inlineText,
    ]
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function VariablePicker(props: VariablePickerProps) {
  const {
    currentNodeId,
    nodes,
    edges,
    nodeRegistry,
    inputDataType,
    inputIsList,
    readOnly,
    inputName,
  } = props;
  const isMulti = props.multiple === true;
  const isReadOnly = !!readOnly;

  const [open, setOpen] = useState(false);
  const setPickerHighlight = useSetAtom(pickerHighlightAtom);

  useEffect(
    () => () => setPickerHighlight(CLEAR_HIGHLIGHT),
    [setPickerHighlight]
  );

  const allOptions = useVariableOptions(
    nodes,
    currentNodeId,
    edges,
    nodeRegistry,
    inputDataType,
    inputName,
    inputIsList
  );

  // Flat lookup by value for quick resolution (metas, selected state)
  const optionsByValue = useMemo(
    () => buildOptionsByValue(allOptions),
    [allOptions]
  );

  const selectedSingle = useMemo(() => {
    if (isMulti || !props.value) return null;
    return optionsByValue.get(props.value) ?? null;
  }, [isMulti, props, optionsByValue]);

  const selectedMulti: Array<{ node: NodeOption; output: OutputOption }> =
    useMemo(() => {
      if (!isMulti) return [];
      return (props.value as string[])
        .map((v) => optionsByValue.get(v))
        .filter((x): x is { node: NodeOption; output: OutputOption } => !!x);
    }, [isMulti, props, optionsByValue]);
  const selectedMultiByNode = useMemo(() => {
    if (!isMulti) return [];
    const byNode = new Map<
      string,
      { node: NodeOption; outputs: OutputOption[] }
    >();
    for (const selected of selectedMulti) {
      const current = byNode.get(selected.node.nodeId);
      if (current) {
        current.outputs.push(selected.output);
      } else {
        byNode.set(selected.node.nodeId, {
          node: selected.node,
          outputs: [selected.output],
        });
      }
    }
    return Array.from(byNode.values());
  }, [isMulti, selectedMulti]);

  const multiSelectedSet = useMemo(
    () => (isMulti ? new Set(props.value as string[]) : new Set<string>()),
    [isMulti, props]
  );

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (isReadOnly) return;
      setOpen(next);
      if (next) {
        setPickerHighlight({
          sourceNodeId: undefined,
          sourceOutputName: undefined,
          targetNodeId: currentNodeId,
          targetInputName: inputName,
        });
      } else {
        setPickerHighlight(CLEAR_HIGHLIGHT);
      }
    },
    [setPickerHighlight, currentNodeId, inputName, isReadOnly]
  );

  const handleSelectSingle = useCallback(
    (optValue: string) => {
      if (isMulti) return;
      const single = props as SingleProps;
      if (optValue === single.value) {
        single.onChange(undefined);
      } else {
        const entry = optionsByValue.get(optValue);
        single.onChange(
          optValue,
          entry
            ? {
                dataType: entry.output.dataType,
                isList: entry.output.isList,
                listDepth: entry.output.listDepth,
              }
            : undefined
        );
      }
      setOpen(false);
      setPickerHighlight(CLEAR_HIGHLIGHT);
    },
    [isMulti, props, optionsByValue, setPickerHighlight]
  );

  const handleToggleMulti = useCallback(
    (optValue: string) => {
      if (!isMulti) return;
      const multi = props as MultiProps;
      const current = multi.value;
      const next = current.includes(optValue)
        ? current.filter((v) => v !== optValue)
        : [...current, optValue];
      const metas: Record<string, OutputMeta> = {};
      for (const v of next) {
        const entry = optionsByValue.get(v);
        if (entry) {
          metas[v] = {
            dataType: entry.output.dataType,
            isList: entry.output.isList,
            listDepth: entry.output.listDepth,
          };
        }
      }
      multi.onChange(next, metas);
    },
    [isMulti, props, optionsByValue]
  );

  const handleClearSingle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isMulti) return;
      (props as SingleProps).onChange(undefined);
    },
    [isMulti, props]
  );

  const handleClearMulti = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!isMulti) return;
      (props as MultiProps).onChange([], {});
    },
    [isMulti, props]
  );

  const handleRowHover = useCallback(
    (nodeId: string | undefined, outputName: string | undefined) => {
      setPickerHighlight((prev) => ({
        ...prev,
        sourceNodeId: nodeId,
        sourceOutputName: outputName,
      }));
    },
    [setPickerHighlight]
  );

  const compatibleNodes = useMemo(
    () => allOptions.filter((n) => n.hasCompatible),
    [allOptions]
  );

  if (isReadOnly) {
    if (isMulti) {
      return selectedMultiByNode.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {(props as MultiProps).placeholder ?? "No available inputs."}
        </p>
      ) : (
        <div className="flex flex-col gap-2.5">
          {selectedMultiByNode.map(({ node, outputs }) => (
            <div
              key={node.nodeId}
              className="rounded-md border border-border/80 bg-background px-2.5 py-2"
            >
              <div className="mb-2 flex items-center gap-2">
                {node.nodeId === "__secrets__" ? (
                  <img
                    src={GLOBAL_VARIABLE_ICON_DATA_URI}
                    alt=""
                    className="size-4 object-contain shrink-0"
                    draggable={false}
                  />
                ) : (
                  <NodeIcon
                    image={node.nodeImage}
                    iconName={node.nodeIconName}
                  />
                )}
                <span className="text-sm font-medium text-muted-foreground">
                  {node.nodeName}
                </span>
              </div>
              <div className="flex flex-col gap-1.5">
                {outputs.map((output) => (
                  <div
                    key={output.value}
                    className="flex items-center justify-between gap-2 rounded-sm px-1.5 py-1"
                  >
                    <p className="min-w-0 truncate text-sm text-foreground">
                      {output.outputLabel}
                    </p>
                    <DataTypeBadge
                      dataType={output.dataType}
                      isList={output.isList}
                      className="shrink-0"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return selectedSingle ? (
      <div className="flex items-center justify-between gap-2 rounded-md border border-border/80 bg-background px-2.5 py-2">
        <div className="min-w-0">
          <p className="truncate text-sm text-foreground">
            {selectedSingle.output.outputLabel}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {selectedSingle.node.nodeName}
          </p>
        </div>
        <DataTypeBadge
          dataType={selectedSingle.output.dataType}
          isList={selectedSingle.output.isList}
          className="shrink-0"
        />
      </div>
    ) : (
      <span className="text-sm text-muted-foreground">No available input.</span>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className={cn(
            "w-full max-w-full min-w-0 shrink h-auto min-h-9 justify-between px-3 py-1.5 font-normal overflow-hidden",
            open && "ring-1 ring-ring"
          )}
        >
          {isMulti ? (
            selectedMulti.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1 min-w-0 overflow-hidden py-0.5">
                {selectedMulti.map(({ node, output }) => (
                  <DataChip
                    key={output.value}
                    sourceImage={node.nodeImage}
                    sourceIconName={node.nodeIconName}
                    sourceColor={node.nodeColor}
                    sourceLabel={node.nodeName}
                    dataType={output.dataType}
                    variableLabel={output.outputLabel}
                    isList={output.isList}
                    size="sm"
                    className="opacity-70"
                  />
                ))}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">
                {(props as MultiProps).placeholder ?? "Select sources…"}
              </span>
            )
          ) : selectedSingle ? (
            <Tooltip delayDuration={400}>
              <TooltipTrigger asChild>
                <div className="min-w-0 overflow-hidden">
                  <DataChip
                    sourceImage={selectedSingle.node.nodeImage}
                    sourceIconName={selectedSingle.node.nodeIconName}
                    sourceColor={selectedSingle.node.nodeColor}
                    sourceLabel={selectedSingle.node.nodeName}
                    dataType={selectedSingle.output.dataType}
                    variableLabel={selectedSingle.output.outputLabel}
                    isList={selectedSingle.output.isList}
                  />
                </div>
              </TooltipTrigger>
              {!open && (
                <BoundVariableTooltipContent
                  variableLabel={selectedSingle.output.outputLabel}
                  dataType={selectedSingle.output.dataType}
                  isList={selectedSingle.output.isList}
                  sourceLabel={selectedSingle.node.nodeName}
                  sourceImage={selectedSingle.node.nodeImage}
                  sourceIconName={selectedSingle.node.nodeIconName}
                  sourceColor={selectedSingle.node.nodeColor}
                  variableRef={selectedSingle.output.value}
                />
              )}
            </Tooltip>
          ) : (
            <span className="text-sm text-muted-foreground">
              Select source…
            </span>
          )}
          <div className="flex items-center gap-1 shrink-0 ml-2">
            {isMulti
              ? selectedMulti.length > 0 && (
                  <span
                    className="opacity-40 hover:opacity-100 cursor-pointer"
                    onClick={handleClearMulti}
                  >
                    <X size={11} />
                  </span>
                )
              : (props as SingleProps).value && (
                  <span
                    className="opacity-40 hover:opacity-100 cursor-pointer"
                    onClick={handleClearSingle}
                  >
                    <X size={11} />
                  </span>
                )}
            <ChevronDown size={13} className="opacity-40" />
          </div>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="p-0 w-[300px] z-5000"
        align="start"
        hasPortal={false}
      >
        {compatibleNodes.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center px-4">
            No compatible variables available.
          </p>
        ) : (
          <Command>
            <CommandInput placeholder="Search variables…" />
            <CommandList className="max-h-[360px] py-1">
              <CommandEmpty>No variables match your search.</CommandEmpty>
              {compatibleNodes.map((node, index) => (
                <React.Fragment key={node.nodeId}>
                  {index > 0 && <CommandSeparator className="my-1" />}
                  <CommandGroup
                    value={node.nodeId}
                    className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pt-2.5 [&_[cmdk-group-heading]]:pb-1.5"
                    heading={
                      <div className="flex items-center gap-2">
                        {node.nodeId === "__secrets__" ? (
                          <img
                            src={GLOBAL_VARIABLE_ICON_DATA_URI}
                            alt=""
                            className="size-4 object-contain shrink-0"
                            draggable={false}
                          />
                        ) : (
                          <NodeIcon
                            image={node.nodeImage}
                            iconName={node.nodeIconName}
                          />
                        )}
                        <span className="text-sm font-medium text-muted-foreground truncate">
                          {node.nodeName}
                        </span>
                      </div>
                    }
                  >
                    {node.outputs
                      .filter((o) => o.compatible)
                      .map((output) => {
                        const isSelected = isMulti
                          ? multiSelectedSet.has(output.value)
                          : (props as SingleProps).value === output.value;
                        const searchValue =
                          `${node.nodeName} ${output.outputLabel} ${output.outputName} ${output.value}`.trim();
                        return (
                          <CommandItem
                            key={output.value}
                            value={searchValue}
                            onSelect={() =>
                              isMulti
                                ? handleToggleMulti(output.value)
                                : handleSelectSingle(output.value)
                            }
                            onMouseEnter={() =>
                              handleRowHover(node.nodeId, output.outputName)
                            }
                            onMouseLeave={() =>
                              handleRowHover(undefined, undefined)
                            }
                            className="pl-9 pr-3 py-2 cursor-pointer flex items-center justify-between gap-2"
                          >
                            {isMulti && (
                              <Checkbox
                                checked={isSelected}
                                className="shrink-0 pointer-events-none"
                                tabIndex={-1}
                              />
                            )}
                            <span className="flex-1 min-w-0 truncate text-sm text-foreground">
                              {output.outputLabel}
                            </span>
                            {output.compatibility === "coerced" && (
                              <CoercedBadge dataType={output.dataType} />
                            )}
                            {output.scope && (
                              <VariableScopeBadge
                                scope={output.scope}
                                size="sm"
                              />
                            )}
                            {output.objectFields?.length ? (
                              <Tooltip delayDuration={300}>
                                <TooltipTrigger asChild>
                                  <span className="shrink-0">
                                    <DataTypeBadge
                                      dataType={output.dataType}
                                      isList={output.isList}
                                    />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="right"
                                  className="max-w-64 p-2"
                                >
                                  <ObjectShapeList
                                    fields={output.objectFields}
                                    dense
                                  />
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <DataTypeBadge
                                dataType={output.dataType}
                                isList={output.isList}
                                className="shrink-0"
                              />
                            )}
                            {!isMulti && isSelected && (
                              <Check
                                size={13}
                                className="shrink-0 text-primary ml-1"
                              />
                            )}
                          </CommandItem>
                        );
                      })}
                  </CommandGroup>
                </React.Fragment>
              ))}
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}
